/**
 * menu module
 * @module TWTmenu
 * @requires TWTmediaQuery
 */

import * as TWTmediaQuery from './twt.media-query';

/**
 * @param  {HTMLElement} element the element in which to search for the first child
 * @return {HTMLElement} the first child
 */
function getFirstChild(element) {
  let firstChild = element.firstChild;

  // skip TextNodes
  while (firstChild != null && firstChild.nodeType === 3) {
    firstChild = firstChild.nextSibling;
  }

  return firstChild;
}

/**
 * @param  {HTMLElement} element the element to unwrap
 * @return {void}
 */
function unwrapElement(element) {
  // get the element's parent node
  const parent = element.parentNode;

  // move all children out of the element
  while (getFirstChild(element)) {
    const firstChild = getFirstChild(element);
    if (firstChild.classList.contains('menu-overflow-inner')) {
      unwrapElement(firstChild);
    } else {
      parent.insertBefore(firstChild, element);
    }
  }

  // remove the empty element
  parent.removeChild(element);
}

/**
 * @class Menu
 * @export
 */
export default class Menu {
  /**
   * Creates a new instance of a menu.
   * @param {HTMLElement} element HTMLElement to make into a menu.
   * @param {Object} options Overrides for the default settings.
   */
  constructor(element, options) {
    this.element = element;
    this.options = Object.assign(Menu.defaults, options);
    this.currentSize = TWTmediaQuery.getCurrentSize();

    // initialize
    this.init();
  }

  /**
   * @param  {Event} event the click event
   * @return {void}
   */
  manageFlyout(event) {
    const sublevel = event.target.closest('[data-sublevel]');
    const level = parseInt(sublevel.dataset.sublevel, 10) || null;
    const flyout = event.target.parentNode.querySelector(`[data-sublevel="${level + 1}"]`);
    const menu = event.target;

    if (sublevel === null || flyout === null) return false;

    menu.classList.toggle(this.options.activeClass);

    if (this.currentSize === 'xs' && this.currentSize === 's') {
      return false;
    }

    // closest parent level 1
    if (level === 1 && event.target.nodeName === 'A' && flyout !== null) {
      if (event.preventDefault) {
        event.preventDefault();
      } else {
        event.returnValue = false; // eslint-disable-line no-param-reassign
      }

      // reset current active
      if (this.activeFlyout !== undefined && this.activeFlyout !== flyout) {
        this.activeFlyout.classList.remove(this.options.activeClass);
        this.activeMenu.classList.remove(this.options.activeClass);
      }

      // toogle class
      flyout.classList.toggle(this.options.activeClass);

      // set current flyout
      this.activeFlyout = flyout;
      this.activeMenu = menu;
    }

    this.manageFlyoutHeight();

    return false;
  }

  /**
   * @return {void}
   */
  manageFlyoutHeight() {
    const flyouts = [].slice.call(this.element.querySelectorAll('[data-sublevel]'));
    const flyoutsHeight = flyouts.reduce((height, flyout) => height + flyout.offsetHeight, 0);
    const spacing = 30;

    const overflowWrapper = document.getElementById(this.options.overflowWrapperID);

    // reset content
    if (overflowWrapper !== null) {
      unwrapElement(overflowWrapper);
    }

    // make content scrollable
    if (this.activeFlyout !== undefined && flyoutsHeight > 0) {
      const flyoutTopPosition = flyouts
        .filter(flyout => flyout.offsetHeight > 0 && flyout.dataset.sublevel === '1')[0]
        .getBoundingClientRect().top;
      const diff = (flyoutTopPosition + flyoutsHeight) - window.innerHeight;
      if (diff > 0) {
        const newContent = `<div id="${this.options.overflowWrapperID}" class="menu-overflow-wrapper row"><div class="menu-overflow-inner column">${this.activeFlyout.innerHTML}</div></div>`;

        // replace content
        this.activeFlyout.innerHTML = newContent;

        // set content height and add class
        const content = document.getElementById(this.options.overflowWrapperID);
        content.style.height = `${content.offsetHeight - diff - spacing}px`;
        content.classList.add('is--overflow');
      }
    }
  }

  /**
   * @return {void}
   */
  registerEventlistener() {
    // viewport
    window.addEventListener('changed.twt.mediaQuery', (event) => {
      this.currentSize = event.detail.newSize;
    });
  }

  /**
   * @return {void}
   */
  init() {
    this.registerEventlistener();
  }
}

Menu.defaults = {
  activeClass: 'is--active',
  overflowWrapperID: 'menuOverflowWrapper',
};



// WEBPACK FOOTER //
// ./assets/js/components/twt.menu.js